import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { userService, NotificationPreferences } from '../services/userService';

const NotificationSettings: React.FC = () => {
  const [preferences, setPreferences] = useState<NotificationPreferences>({
    emailNotificationsEnabled: true,
    reviewRemindersEnabled: true,
    preferredReminderTime: '08:00',
    timezone: 'UTC',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadPreferences();
  }, []);

  const loadPreferences = async () => {
    try {
      const data = await userService.getNotificationPreferences();
      setPreferences(data);
    } catch (error) {
      console.error('Error loading notification preferences:', error);
      toast.error('Erro ao carregar preferências de notificação');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await userService.updateNotificationPreferences(preferences);
      toast.success('Preferências de notificação atualizadas com sucesso!');
    } catch (error) {
      console.error('Error updating notification preferences:', error);
      toast.error('Erro ao atualizar preferências de notificação');
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field: keyof NotificationPreferences, value: boolean | string) => {
    setPreferences(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  if (loading) {
    return (
      <div className="animate-pulse">
        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-4"></div>
        <div className="space-y-3">
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-2/3"></div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h3 className="text-base sm:text-lg leading-6 font-medium text-gray-900 dark:text-white mb-3 sm:mb-4">
        Configurações de Notificação
      </h3>
      
      <div className="space-y-4 sm:space-y-6">
        {/* Email Notifications Toggle */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0">
          <div className="flex-1">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Notificações por Email
            </label>
            <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              Receber notificações gerais por email
            </p>
          </div>
          <div className="sm:ml-4 self-end sm:self-auto">
            <button
              type="button"
              className={`${
                preferences.emailNotificationsEnabled
                  ? 'bg-indigo-600'
                  : 'bg-gray-200 dark:bg-gray-700'
              } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2`}
              onClick={() => handleInputChange('emailNotificationsEnabled', !preferences.emailNotificationsEnabled)}
            >
              <span
                className={`${
                  preferences.emailNotificationsEnabled ? 'translate-x-5' : 'translate-x-0'
                } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
              />
            </button>
          </div>
        </div>

        {/* Review Reminders Toggle */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0">
          <div className="flex-1">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Lembretes de Revisão
            </label>
            <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              Receber lembretes quando houver cards ou quizzes prontos para revisar
            </p>
          </div>
          <div className="sm:ml-4 self-end sm:self-auto">
            <button
              type="button"
              disabled={!preferences.emailNotificationsEnabled}
              className={`${
                preferences.reviewRemindersEnabled && preferences.emailNotificationsEnabled
                  ? 'bg-indigo-600'
                  : 'bg-gray-200 dark:bg-gray-700'
              } relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50`}
              onClick={() => handleInputChange('reviewRemindersEnabled', !preferences.reviewRemindersEnabled)}
            >
              <span
                className={`${
                  preferences.reviewRemindersEnabled && preferences.emailNotificationsEnabled
                    ? 'translate-x-5'
                    : 'translate-x-0'
                } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
              />
            </button>
          </div>
        </div>

        {/* Preferred Reminder Time */}
        {preferences.emailNotificationsEnabled && preferences.reviewRemindersEnabled && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Horário Preferido para Lembretes
            </label>
            <input
              type="time"
              value={preferences.preferredReminderTime}
              onChange={(e) => handleInputChange('preferredReminderTime', e.target.value)}
              className="block w-full sm:w-auto px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-800 dark:text-white text-sm"
            />
            <p className="mt-1 text-xs sm:text-sm text-gray-500 dark:text-gray-400">
              Defina o horário preferido para receber lembretes de revisão
            </p>
          </div>
        )}

        {/* Timezone */}
        {preferences.emailNotificationsEnabled && preferences.reviewRemindersEnabled && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Fuso Horário
            </label>
            <select
              value={preferences.timezone}
              onChange={(e) => handleInputChange('timezone', e.target.value)}
              className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-800 dark:text-white sm:text-sm"
            >
              <option value="UTC">UTC</option>
              <option value="America/Sao_Paulo">São Paulo (UTC-3)</option>
              <option value="America/New_York">Nova York (UTC-5/-4)</option>
              <option value="Europe/London">Londres (UTC+0/+1)</option>
              <option value="Europe/Paris">Paris (UTC+1/+2)</option>
              <option value="Asia/Tokyo">Tóquio (UTC+9)</option>
              <option value="Australia/Sydney">Sydney (UTC+10/+11)</option>
            </select>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Selecione seu fuso horário para receber lembretes no horário correto
            </p>
          </div>
        )}

        {/* Save Button */}
        <div className="pt-4">
          <button
            type="button"
            disabled={saving}
            onClick={handleSave}
            className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Salvando...' : 'Salvar Preferências'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NotificationSettings;